import { Link } from 'react-router-dom';
import type { Client, Contact, Project, Task } from '../../types/crm';
import { usePortalPath } from '../../contexts/PortalContext';
import { accountDetailPath } from '../../lib/portal';
import { idsMatch } from '../../utils/crmHelpers';
import { clientTypeaheadLabel } from '../accounts/AccountTypeahead';

type Props = {
  client: Client | null;
  contacts: Contact[];
  projects: Project[];
  tasks: Task[];
  selectedContactId: string;
  requesterEmail?: string;
  onEditLink: () => void;
};

function contactName(c: Contact): string {
  return [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email || 'Contact';
}

export function TicketAccountPanel({
  client,
  contacts,
  projects,
  tasks,
  selectedContactId,
  requesterEmail,
  onEditLink,
}: Props) {
  const projectsPath = usePortalPath('projects');
  const tasksPath = usePortalPath('tasks');

  const openProjects = projects.filter((p) => p.status !== 'Completed');
  const openTasks = tasks.filter((t) => t.status !== 'Done');
  const selectedContact =
    contacts.find((c) => idsMatch(c.id, selectedContactId)) ?? null;

  return (
    <aside className="ticket-account-panel" aria-label="Linked account details">
      <div className="ticket-account-panel__head">
        <h3 className="ticket-account-panel__title">Account</h3>
        <div className="ticket-account-panel__head-actions">
          <button type="button" className="ticket-account-panel__link-btn" onClick={onEditLink}>
            Edit
          </button>
          {client?.code ? (
            <Link className="ticket-account-panel__link" to={accountDetailPath(client.code)}>
              Open
            </Link>
          ) : null}
        </div>
      </div>

      {!client ? (
        <p className="ticket-account-panel__empty">
          No account linked
          {requesterEmail ? (
            <>
              {' '}
              for <strong>{requesterEmail}</strong>
            </>
          ) : null}
          .
        </p>
      ) : (
        <div className="ticket-account-panel__summary">
          <p className="ticket-account-panel__current">{clientTypeaheadLabel(client)}</p>
          <p className="ticket-account-panel__contact">
            {selectedContact ? (
              <>
                {contactName(selectedContact)}
                {selectedContact.email ? ` · ${selectedContact.email}` : ''}
              </>
            ) : (
              <span className="ticket-account-panel__empty">
                No contact linked
                {requesterEmail ? ` · ${requesterEmail}` : ''}
              </span>
            )}
          </p>
        </div>
      )}

      {client ? (
        <>
          <section className="ticket-account-panel__section">
            <h4>Projects ({openProjects.length})</h4>
            {openProjects.length === 0 ? (
              <p className="ticket-account-panel__empty">No open projects.</p>
            ) : (
              <ul>
                {openProjects.slice(0, 5).map((p) => (
                  <li key={p.id}>
                    <Link to={`${projectsPath}/${p.id}`}>{p.title || p.reference_no || p.id}</Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="ticket-account-panel__section">
            <h4>Tasks ({openTasks.length})</h4>
            {openTasks.length === 0 ? (
              <p className="ticket-account-panel__empty">No open tasks.</p>
            ) : (
              <ul>
                {openTasks.slice(0, 5).map((t) => (
                  <li key={t.id}>
                    <Link to={`${tasksPath}/${t.id}`}>{t.title || t.id}</Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      ) : null}
    </aside>
  );
}
